import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { CONSTANTS } from "../data";
import { ASSETS } from "../assetManifest.js";

interface LatteArtGameProps {
  active: boolean;
  onComplete: (accuracy: number) => void;
  onExit: () => void;
}

// Haptic feedback helper
const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if (CONSTANTS.TOUCH_INTERACTION.HAPTIC_FEEDBACK_ENABLED && 'vibrate' in navigator) {
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [30]
    };
    navigator.vibrate(patterns[type]);
  }
};

// Smooth line drawing with touch optimization
const smoothPath = (points: { x: number; y: number }[], smoothing: number = CONSTANTS.TOUCH_INTERACTION.GESTURE_SENSITIVITY.LATTE_ART_SMOOTHING) => {
  if (points.length < 3) return points;
  
  const smoothed = [points[0]];
  
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    
    smoothed.push({
      x: curr.x + (prev.x + next.x - 2 * curr.x) * smoothing,
      y: curr.y + (prev.y + next.y - 2 * curr.y) * smoothing,
    });
  }
  
  smoothed.push(points[points.length - 1]);
  return smoothed;
};

const LatteArtGame: React.FC<LatteArtGameProps> = ({
  active,
  onComplete,
  onExit,
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [touchFeedback, setTouchFeedback] = useState(false);
  const pathRef = useRef<{ x: number; y: number }[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>();
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    context.fillStyle = "#F5F3F1"; // bg-main
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw target line
    context.strokeStyle = "rgba(107, 93, 91, 0.4)"; // text-secondary
    context.lineWidth = 4;
    context.beginPath();
    context.moveTo(0, canvas.height / 2);
    context.lineTo(canvas.width, canvas.height / 2);
    context.stroke();

    const path = pathRef.current;
    if (path.length < 2) return;

    // Smooth the path for better touch experience
    const smoothedPath = smoothPath(path);

    context.strokeStyle = "#3D2C2A"; // text-primary
    context.lineWidth = 6; // Slightly thicker for touch
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    
    if (smoothedPath.length > 0) {
      context.moveTo(smoothedPath[0].x, smoothedPath[0].y);
      for (let i = 1; i < smoothedPath.length; i++) {
        context.lineTo(smoothedPath[i].x, smoothedPath[i].y);
      }
    }
    context.stroke();
  }, []);

  useEffect(() => {
    if (!active) return;

    pathRef.current = [];
    lastPointRef.current = null;
    setIsDrawing(false);
    const startTime = Date.now();

    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    }
    draw();

    const gameLoop = () => {
      if (Date.now() - startTime > CONSTANTS.LATTE_ART.gestureDuration) {
        const path = pathRef.current;
        let totalDeviation = 0;
        const targetY = canvasRef.current ? canvasRef.current.height / 2 : 50;
        if (path.length > 0) {
          path.forEach((p) => {
            totalDeviation += Math.abs(p.y - targetY);
          });
          const avgDeviation = totalDeviation / path.length;
          const maxDeviation = canvasRef.current
            ? canvasRef.current.height * 0.75
            : targetY;
          const normalizedDeviation = Math.min(1, avgDeviation / maxDeviation);
          const accuracy = 100 * (1 - normalizedDeviation);
          
          // Provide haptic feedback based on performance
          if (accuracy >= CONSTANTS.LATTE_ART.perfectThreshold) {
            triggerHapticFeedback('heavy');
          } else if (accuracy >= CONSTANTS.LATTE_ART.goodThreshold) {
            triggerHapticFeedback('medium');
          } else {
            triggerHapticFeedback('light');
          }
          
          onComplete(accuracy);
        } else {
          onComplete(0);
        }
      } else {
        animationFrameId.current = requestAnimationFrame(gameLoop);
      }
    };

    animationFrameId.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    };
  }, [active, onComplete, draw]);

  const getPointerPosition = (e: React.MouseEvent | React.TouchEvent) => {
    if (!canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const addPoint = useCallback((pos: { x: number; y: number }) => {
    // Filter out points that are too close together for smoother lines
    if (lastPointRef.current) {
      const distance = Math.sqrt(
        Math.pow(pos.x - lastPointRef.current.x, 2) + 
        Math.pow(pos.y - lastPointRef.current.y, 2)
      );
      
      if (distance < CONSTANTS.TOUCH_INTERACTION.GESTURE_SENSITIVITY.LATTE_ART_MIN_DISTANCE) {
        return;
      }
    }
    
    pathRef.current.push(pos);
    lastPointRef.current = pos;
    draw();
  }, [draw]);

  const handlePointerDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!active) return;
    e.preventDefault();
    e.stopPropagation();
    
    setIsDrawing(true);
    setTouchFeedback(true);
    triggerHapticFeedback('light');
    
    const pos = getPointerPosition(e);
    if (pos) {
      pathRef.current = [pos];
      lastPointRef.current = pos;
      draw();
    }
    
    setTimeout(() => setTouchFeedback(false), CONSTANTS.TOUCH_INTERACTION.TOUCH_FEEDBACK_DURATION);
  }, [active, draw]);

  const handlePointerMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!active || !isDrawing) return;
    e.preventDefault();
    e.stopPropagation();
    
    const pos = getPointerPosition(e);
    if (pos) {
      addPoint(pos);
    }
  }, [active, isDrawing, addPoint]);

  const handlePointerUp = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDrawing(false);
    setTouchFeedback(false);
  }, []);

  const handleExit = useCallback(() => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }
    triggerHapticFeedback('light');
    onExit();
  }, [onExit]);

  if (!active) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={handleExit}
      style={{ touchAction: 'none' }}
    >
      <div 
        className="bg-surface p-6 rounded-lg max-w-md w-full mx-4 select-none shadow-xl border border-border-color"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-text-primary">🎨 Latte Art</h3>
          <button
            onClick={handleExit}
            className="text-text-secondary hover:text-text-primary p-2 rounded-full hover:bg-bg-main active:scale-95 transition-all"
            style={{
              minHeight: `${CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE}px`,
              minWidth: `${CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE}px`,
            }}
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-center text-text-secondary mb-4">Draw a steady, straight line across!</p>
        <div
          className={`h-48 bg-bg-main rounded-lg border-2 border-border-color relative cursor-crosshair touch-none ${
            touchFeedback ? 'ring-2 ring-primary-action/50' : ''
          }`}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          onTouchCancel={handlePointerUp}
          style={{
            touchAction: 'none',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
        >
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
          
          {/* Visual feedback for touch */}
          {isDrawing && (
            <div className="absolute top-2 left-2 bg-primary-action text-white px-2 py-1 rounded text-xs font-bold">
              Drawing...
            </div>
          )}
        </div>
        <div className="mt-4 text-center text-sm text-text-secondary">
          Perfect: 90%+ | Good: 70%+ | Messy: &lt;70%
        </div>
        <div className="mt-4 text-center">
          <button
            onClick={handleExit}
            className="bg-text-secondary text-white px-6 py-3 rounded-lg hover:opacity-90 active:scale-95 transition-all"
            style={{
              minHeight: `${CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE}px`,
              touchAction: 'manipulation',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default LatteArtGame;
